import { BreadcrumbItem, Breadcrumbs, Container, Picture } from '@troon/ui';
import { createEffect, createSignal, For, mergeProps, Show, splitProps } from 'solid-js';
import { twJoin } from '@troon/tailwind-preset/merge';
import { useWindowScrollPosition } from '@solid-primitives/scroll';
import { ImageOverlay } from '../image-overlay';
import type { ComponentProps, ParentProps } from 'solid-js';

type Props = ParentProps<{
	breadcrumbs?: Array<{ href: string; title: string }>;
	overlay?: 'brand' | 'left-right' | 'darker';
	darker?: boolean;
	fullWidth?: boolean;
	src?: string | null;
	alt?: string;
	size?: 'default' | 'large' | 'small';
}> &
	Partial<ComponentProps<typeof Picture>>;

export function Hero(inputProps: Props) {
	const props = mergeProps({ size: 'default' }, inputProps);
	const [, img] = splitProps(props, ['src', 'alt', 'size', 'children', 'breadcrumbs']);
	const scroll = useWindowScrollPosition();
	const [picture, setPicture] = createSignal<HTMLImageElement>();

	createEffect(() => {
		const pic = picture();
		if (pic) {
			pic.style.transform = `scale(${Math.max(scroll.y / window.innerWidth + 1, 1)})`;
		}
	});

	return (
		<div
			class={twJoin(
				'relative mb-12 flex flex-col items-stretch overflow-hidden bg-linear-to-r from-neutral-950 to-brand-700 text-balance',
				props.size === 'small' ? 'min-h-24' : 'sm:min-h-72 md:min-h-96',
				props.breadcrumbs && props.size !== 'small' ? 'min-h-96' : props.size !== 'small' ? 'min-h-64' : '',
			)}
		>
			<Show when={props.src}>
				<Picture
					preload
					ref={setPicture}
					crop="center"
					{...(img as ComponentProps<typeof Picture>)}
					src={props.src!}
					alt={props.alt ?? ''}
					width={1280}
					height={props.size === 'small' ? 100 : props.size === 'large' ? 819 : 388}
					sizes="100vw"
					class={twJoin(
						'absolute size-full bg-center object-cover',
						props.darker || props.overlay === 'darker' ? 'brightness-70' : 'brightness-75',
					)}
					loading="eager"
					draggable={false}
				/>
				<Show when={props.overlay === 'left-right'}>
					<div class="absolute inset-0 bg-linear-to-t from-black/80 via-black/10 to-black/0 md:bg-linear-to-r" />
				</Show>
			</Show>
			<Show when={props.breadcrumbs}>
				<Container class="z-20 mt-20 -mb-20 text-white">
					<Breadcrumbs appearance="current">
						<For each={props.breadcrumbs}>
							{(crumb) => <BreadcrumbItem href={crumb.href}>{crumb.title}</BreadcrumbItem>}
						</For>
					</Breadcrumbs>
				</Container>
			</Show>

			<div
				class={twJoin(
					'z-10 mx-auto flex h-full grow flex-col items-center justify-center gap-4 px-4 text-center text-white',
					props.size !== 'small' && 'my-16 pt-16',
					props.fullWidth ? 'w-full' : 'max-w-3xl',
				)}
			>
				{props.children}
			</div>
			<ImageOverlay class="relative top-1" />
		</div>
	);
}
